import { useEffect, useRef } from "react";
import { ActionIcon, NativeSelect } from "@mantine/core";

import { NewsletterIcon, type NewsletterIconId } from "./newsletterIcons";

type RichTextEditorMode = "full" | "signature";

type RichTextEditorProps = {
  editorId: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  mode?: RichTextEditorMode;
  onRequestInsertImage?: (insertImage: (url: string, altText?: string) => void) => void;
};

function ToolbarButton({
  iconId,
  label,
  onClick,
}: {
  iconId: NewsletterIconId;
  label: string;
  onClick: () => void;
}) {
  return (
    <ActionIcon
      type="button"
      className="toolbar-button newsletter-rte-action"
      aria-label={label}
      title={label}
      variant="subtle"
      color="dark"
      radius="xl"
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      <NewsletterIcon iconId={iconId} />
    </ActionIcon>
  );
}

export function RichTextEditor({
  editorId,
  value,
  onChange,
  placeholder,
  mode = "full",
  onRequestInsertImage,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const selectionRef = useRef<Range | null>(null);

  function supportsExecCommand() {
    return typeof document.execCommand === "function";
  }

  useEffect(() => {
    if (!supportsExecCommand()) {
      return;
    }
    document.execCommand("styleWithCSS", false, "true");
  }, []);

  useEffect(() => {
    if (!editorRef.current) {
      return;
    }

    if (editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  function selectionBelongsToEditor() {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) {
      return false;
    }
    const range = selection.getRangeAt(0);
    return editor.contains(range.commonAncestorContainer);
  }

  function saveSelection() {
    const selection = window.getSelection();
    if (!selectionBelongsToEditor() || !selection || selection.rangeCount === 0) {
      return;
    }
    selectionRef.current = selection.getRangeAt(0).cloneRange();
  }

  function focusEditorEnd() {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection) {
      return;
    }

    editor.focus();
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  function restoreSelection() {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection) {
      return;
    }

    editor.focus();
    if (!selectionRef.current) {
      focusEditorEnd();
      return;
    }

    selection.removeAllRanges();
    selection.addRange(selectionRef.current);
  }

  function applyCommand(command: string, commandValue: string | null = null) {
    if (!supportsExecCommand()) {
      editorRef.current?.focus();
      return;
    }
    restoreSelection();
    document.execCommand(command, false, commandValue ?? undefined);
    saveSelection();
    onChange(editorRef.current?.innerHTML || "");
  }

  function handleAction(action: "createLink" | "insertImage" | "removeFormat") {
    if (action === "createLink") {
      const url = window.prompt("URL du lien");
      if (url) {
        applyCommand("createLink", url);
      }
      return;
    }

    if (action === "insertImage") {
      if (onRequestInsertImage) {
        onRequestInsertImage((url) => {
          if (url) {
            applyCommand("insertImage", url);
          }
        });
        return;
      }
      const url = window.prompt("URL publique de l'image");
      if (url) {
        applyCommand("insertImage", url);
      }
      return;
    }

    applyCommand("removeFormat");
  }

  const compact = mode === "signature";

  return (
    <div className={`editor-block${compact ? " editor-block-compact" : ""}`}>
      <div className="editor-toolbar newsletter-rte-toolbar">
        {mode === "full" ? (
          <>
            <div className="toolbar-dropdown">
              <NativeSelect
                defaultValue="Arial"
                aria-label="Police"
                onMouseDown={(event) => event.preventDefault()}
                onChange={(event) => applyCommand("fontName", event.target.value)}
                classNames={{
                  root: "toolbar-select-root",
                  input: "toolbar-select-input",
                  section: "toolbar-select-section",
                }}
                data={[
                  { value: "Arial", label: "Sans Serif" },
                  { value: "Georgia", label: "Serif" },
                  { value: "Courier New", label: "Monospace" },
                ]}
              />
            </div>
            <span className="toolbar-divider" aria-hidden="true"></span>
            <div className="toolbar-dropdown toolbar-dropdown-small">
              <NativeSelect
                defaultValue="p"
                aria-label="Style de texte"
                onMouseDown={(event) => event.preventDefault()}
                onChange={(event) => applyCommand("formatBlock", event.target.value)}
                classNames={{
                  root: "toolbar-select-root",
                  input: "toolbar-select-input",
                  section: "toolbar-select-section",
                }}
                data={[
                  { value: "p", label: "Texte" },
                  { value: "h2", label: "Titre" },
                  { value: "blockquote", label: "Citation" },
                ]}
              />
            </div>
          </>
        ) : null}

        <div className="toolbar-dropdown toolbar-dropdown-small">
          <NativeSelect
            defaultValue="3"
            aria-label="Taille de texte"
            onMouseDown={(event) => event.preventDefault()}
            onChange={(event) => applyCommand("fontSize", event.target.value)}
            classNames={{
              root: "toolbar-select-root",
              input: "toolbar-select-input",
              section: "toolbar-select-section",
            }}
            data={[
              { value: "2", label: "S" },
              { value: "3", label: "M" },
              { value: "4", label: "L" },
              { value: "5", label: "XL" },
              ...(mode === "full" ? [{ value: "6", label: "XXL" }] : []),
            ]}
          />
        </div>

        <span className="toolbar-divider" aria-hidden="true"></span>
        <ToolbarButton iconId="icon-format-bold" label="Gras" onClick={() => applyCommand("bold")} />
        <ToolbarButton iconId="icon-format-italic" label="Italique" onClick={() => applyCommand("italic")} />
        <ToolbarButton iconId="icon-format-underline" label="Souligné" onClick={() => applyCommand("underline")} />
        <label className="toolbar-color" title="Couleur du texte">
          <span className="toolbar-color-swatch">A</span>
          <input
            type="color"
            value="#1f2a44"
            aria-label="Couleur du texte"
            onMouseDown={(event) => event.preventDefault()}
            onChange={(event) => applyCommand("foreColor", event.target.value)}
          />
        </label>

        {mode === "full" ? (
          <>
            <span className="toolbar-divider" aria-hidden="true"></span>
            <ToolbarButton iconId="icon-align-left" label="Aligner à gauche" onClick={() => applyCommand("justifyLeft")} />
            <ToolbarButton iconId="icon-align-center" label="Centrer" onClick={() => applyCommand("justifyCenter")} />
            <ToolbarButton
              iconId="icon-list"
              label="Liste à puces"
              onClick={() => applyCommand("insertUnorderedList")}
            />
            <ToolbarButton
              iconId="icon-list-ordered"
              label="Liste numérotée"
              onClick={() => applyCommand("insertOrderedList")}
            />
            <ToolbarButton iconId="icon-quote" label="Citation" onClick={() => applyCommand("formatBlock", "blockquote")} />
          </>
        ) : null}

        <span className="toolbar-divider" aria-hidden="true"></span>
        <ToolbarButton iconId="icon-link-2" label="Ajouter un lien" onClick={() => handleAction("createLink")} />
        {mode === "full" ? (
          <ToolbarButton iconId="icon-image" label="Ajouter une image" onClick={() => handleAction("insertImage")} />
        ) : null}
        <ToolbarButton
          iconId="icon-erase"
          label="Nettoyer la mise en forme"
          onClick={() => handleAction("removeFormat")}
        />
      </div>
      <div
        id={editorId}
        ref={editorRef}
        className={`editor${compact ? " editor-compact" : ""}`}
        contentEditable="true"
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={() => {
          saveSelection();
          onChange(editorRef.current?.innerHTML || "");
        }}
        onBlur={saveSelection}
        onFocus={saveSelection}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
      />
    </div>
  );
}
